import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { Modal } from '../../common/Modal';
import type { HorseProfile } from '../../../types/horseProfile';
import { HorseProfileView } from './HorseProfileView';
import { HorseProfileEditView } from './HorseProfileEditView';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';

interface HorseProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  horse: HorseProfile | null;
  canEdit: boolean;
}

export const HorseProfileModal: React.FC<HorseProfileModalProps> = ({
  isOpen,
  onClose,
  horse,
  canEdit
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingSection, setEditingSection] = useState<keyof HorseProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { features } = useFeatureFlags();

  const handleSave = async (updatedProfile: Partial<HorseProfile>) => {
    if (!horse) return;

    try {
      const horseRef = doc(db, 'horses', horse.id);
      await updateDoc(horseRef, {
        ...updatedProfile,
        lastUpdated: new Date().toISOString(),
        isPublic: features.publicProfiles
      });
      setIsEditing(false);
      setEditingSection(null);
      setError(null);
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Änderungen konnten nicht gespeichert werden');
    }
  };

  const renderFooter = () => {
    if (!isEditing) return null;

    return (
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <button
          onClick={() => {
            setIsEditing(false);
            setEditingSection(null);
          }}
          className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Abbrechen
        </button>
        <button
          form="profile-edit-form"
          type="submit"
          className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors"
        >
          Speichern
        </button>
      </div>
    );
  };

  if (!horse) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${horse.name}${features.publicProfiles ? ' (Öffentliches Profil)' : ''}`}
      footer={renderFooter()}
      size="xl"
    >
      <div className="space-y-4">
        {isEditing ? (
          <HorseProfileEditView
            horse={horse}
            section={editingSection}
            onSave={handleSave}
            onCancel={() => {
              setIsEditing(false);
              setEditingSection(null);
            }}
          />
        ) : (
          <HorseProfileView
            horse={horse}
            onEdit={canEdit ? (section) => {
              setEditingSection(section);
              setIsEditing(true);
            } : undefined}
          />
        )}

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
            <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};